import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { useContacts, useTicketList } from '../api/queries.js';
import { useAuthStore } from '../auth/auth-store.js';

export function GlobalSearch({ onClose }: { onClose: () => void }): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');

  const ticketsQ = useTicketList(slug, q ? { search: q } : {});
  const contactsQ = useContacts(slug, q);

  const tickets = (ticketsQ.data ?? []).slice(0, 5);
  const contacts = (contactsQ.data ?? []).slice(0, 5);

  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function goTicket(id: string): void {
    void navigate({ to: '/inbox', search: { ticket: id } });
    onClose();
  }

  function goContact(id: string): void {
    void navigate({ to: '/contacts', search: { id } });
    onClose();
  }

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 900,
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 560,
          maxWidth: '90vw',
          background: 'var(--inbx-bg)',
          borderRadius: 12,
          boxShadow: '0 8px 40px rgba(0,0,0,0.24)',
          zIndex: 901,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            borderBottom: '1px solid var(--inbx-border)',
          }}
        >
          <i className="ti-search" style={{ color: 'var(--inbx-text-tertiary)', fontSize: 18 }} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск тикетов, контактов…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: 16,
              background: 'transparent',
              color: 'var(--inbx-text)',
            }}
          />
          <kbd
            style={{
              fontSize: 11,
              color: 'var(--inbx-text-tertiary)',
              background: 'var(--inbx-bg-secondary)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            Esc
          </kbd>
        </div>

        {q.length >= 2 && (tickets.length > 0 || contacts.length > 0) && (
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {tickets.length > 0 && (
              <Section label="Тикеты">
                {tickets.map((t) => (
                  <ResultRow
                    key={t.id}
                    icon="ti-ticket"
                    primary={t.subject ?? `Тикет #${t.number}`}
                    secondary={t.status}
                    onClick={() => goTicket(t.id)}
                  />
                ))}
              </Section>
            )}
            {contacts.length > 0 && (
              <Section label="Контакты">
                {contacts.map((c) => (
                  <ResultRow
                    key={c.id}
                    icon="ti-user"
                    primary={c.name ?? c.email ?? c.id}
                    secondary={c.email ?? c.phone ?? ''}
                    onClick={() => goContact(c.id)}
                  />
                ))}
              </Section>
            )}
          </div>
        )}

        {q.length >= 2 && tickets.length === 0 && contacts.length === 0 && !ticketsQ.isLoading && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--inbx-text-tertiary)' }}>
            Ничего не найдено по запросу «{q}»
          </div>
        )}

        {q.length < 2 && (
          <div style={{ padding: '8px 0' }}>
            <QuickAction
              icon="ti-inbox"
              label="Открыть Inbox"
              onClick={() => {
                void navigate({ to: '/inbox' });
                onClose();
              }}
            />
            <QuickAction
              icon="ti-ticket"
              label="Все тикеты"
              onClick={() => {
                void navigate({ to: '/tickets' });
                onClose();
              }}
            />
            <QuickAction
              icon="ti-users"
              label="Контакты"
              onClick={() => {
                void navigate({ to: '/contacts' });
                onClose();
              }}
            />
            <QuickAction
              icon="ti-chart-bar"
              label="Аналитика"
              onClick={() => {
                void navigate({ to: '/analytics' });
                onClose();
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div
        style={{
          padding: '6px 16px 2px',
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--inbx-text-tertiary)',
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function ResultRow({
  icon,
  primary,
  secondary,
  onClick,
}: {
  icon: string;
  primary: string;
  secondary: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '8px 16px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        color: 'inherit',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'var(--inbx-bg-secondary)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      <i className={icon} style={{ color: 'var(--inbx-accent)', fontSize: 16 }} />
      <div>
        <div style={{ fontWeight: 500 }}>{primary}</div>
        {secondary && (
          <div style={{ fontSize: 'var(--inbx-fz-xs)', color: 'var(--inbx-text-tertiary)' }}>
            {secondary}
          </div>
        )}
      </div>
    </button>
  );
}

function QuickAction({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '9px 16px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        color: 'inherit',
        fontSize: 'var(--inbx-fz-md)',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'var(--inbx-bg-secondary)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      <i className={icon} style={{ color: 'var(--inbx-accent)', fontSize: 16 }} />
      {label}
    </button>
  );
}
